import { useEffect, useRef, useState } from 'react';
import { api } from './api';

const WS_BASE = process.env.REACT_APP_WS_API_BASE || 'ws://localhost:8081';
const MAX_DURATION_MS = 10000;
const MAX_AUDIO_BYTES = 256 * 1024;

const blobToBase64 = async (blob) => {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = '';
  for (let offset = 0; offset < bytes.length; offset += 8192) binary += String.fromCharCode(...bytes.subarray(offset, offset + 8192));
  return window.btoa(binary);
};

const recordingMIME = () => {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4;codecs=mp4a.40.2', 'audio/mp4', 'audio/ogg;codecs=opus'];
  return candidates.find((mime) => window.MediaRecorder?.isTypeSupported?.(mime)) || '';
};

export const useVoiceMessages = (enabled, onMessage) => {
  const callbackRef = useRef(onMessage);
  useEffect(() => { callbackRef.current = onMessage; }, [onMessage]);

  useEffect(() => {
    if (!enabled) return undefined;
    let socket;
    let reconnectTimer;
    let closed = false;
    const connect = () => {
      socket = new WebSocket(`${WS_BASE}/pixel-battle/voice/ws`);
      socket.onmessage = ({ data }) => {
        try {
          const event = JSON.parse(data);
          if (event.type === 'voice' && event.message?.audio) callbackRef.current?.(event.message);
        } catch (_) { /* ignore malformed realtime messages */ }
      };
      socket.onclose = () => { if (!closed) reconnectTimer = window.setTimeout(connect, 2000); };
    };
    connect();
    return () => {
      closed = true;
      window.clearTimeout(reconnectTimer);
      socket?.close();
    };
  }, [enabled]);
};

export const VoiceReply = ({ sequence, onInteraction, onSent }) => {
  const [status, setStatus] = useState('idle');
  const [elapsed, setElapsed] = useState(0);
  const [recording, setRecording] = useState(null);
  const [error, setError] = useState('');
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const stopTimerRef = useRef(null);
  const tickTimerRef = useRef(null);

  const releaseStream = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    window.clearTimeout(stopTimerRef.current);
    window.clearInterval(tickTimerRef.current);
  };

  useEffect(() => () => {
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
    releaseStream();
    if (recording?.url) URL.revokeObjectURL(recording.url);
  }, [recording?.url]);

  const stop = () => {
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
  };

  const start = async () => {
    onInteraction?.();
    setError('');
    if (!window.MediaRecorder || !navigator.mediaDevices?.getUserMedia) {
      setError('Запись голоса не поддерживается браузером');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      streamRef.current = stream;
      const mimeType = recordingMIME();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType, audioBitsPerSecond: 48000 } : undefined);
      const chunks = [];
      const startedAt = Date.now();
      recorderRef.current = recorder;
      recorder.ondataavailable = (event) => { if (event.data.size) chunks.push(event.data); };
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: recorder.mimeType || mimeType || 'audio/webm' });
        releaseStream();
        if (!blob.size || blob.size > MAX_AUDIO_BYTES) {
          setStatus('idle');
          setError('Запись получилась слишком большой');
          return;
        }
        const url = URL.createObjectURL(blob);
        setRecording({ blob, url, durationMS: Math.min(MAX_DURATION_MS, Date.now() - startedAt), mimeType: blob.type });
        setStatus('ready');
      };
      recorder.start(250);
      setElapsed(0);
      setStatus('recording');
      tickTimerRef.current = window.setInterval(() => setElapsed(Math.min(10, Math.ceil((Date.now() - startedAt) / 1000))), 250);
      stopTimerRef.current = window.setTimeout(stop, MAX_DURATION_MS);
    } catch (_) {
      releaseStream();
      setError('Не удалось получить доступ к микрофону');
    }
  };

  const discard = () => {
    if (recording?.url) URL.revokeObjectURL(recording.url);
    setRecording(null);
    setStatus('idle');
    setError('');
  };

  const send = async () => {
    if (!recording) return;
    onInteraction?.();
    setStatus('sending');
    setError('');
    try {
      const messageID = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      await api('/pixel-battle/voice', { method: 'POST', body: JSON.stringify({ placement_sequence: sequence, message_id: messageID, mime_type: recording.mimeType, duration_ms: Math.max(1, recording.durationMS), audio: await blobToBase64(recording.blob) }) });
      setStatus('sent');
      window.setTimeout(() => onSent?.(), 1200);
    } catch (requestError) {
      setStatus('ready');
      setError(requestError.status === 503 ? 'Пользователь уже покинул Pixel Battle' : requestError.message);
    }
  };

  return <div className="voice-reply">
    {status === 'idle' && <button className="voice-record-button" onClick={start}>● Записать ответ</button>}
    {status === 'recording' && <button className="voice-record-button recording" onClick={stop}><i />Остановить · {elapsed}/10</button>}
    {(status === 'ready' || status === 'sending') && <><audio src={recording.url} controls preload="metadata" /><div className="voice-reply-actions"><button onClick={discard} disabled={status === 'sending'}>Удалить</button><button onClick={send} disabled={status === 'sending'}>{status === 'sending' ? 'Отправляем…' : 'Отправить'}</button></div></>}
    {status === 'sent' && <span className="voice-sent">Голосовое доставлено</span>}
    {error && <small className="voice-error">{error}</small>}
  </div>;
};

